/**
 * What the Tickets page's status, priority and search boxes keep.
 *
 * Applied in the browser over the list the server returned for the chosen customer: the list is
 * already capped and fetched whole, and a search box that waits on a round trip per keystroke is
 * not what anyone typing a ticket number wants.
 */
import type { TicketDto, TicketPriority, TicketStatus } from './contracts.gen.js';
import { ALL_TICKET_STATUSES, isFinished, statusLabel } from './rules.js';

export type StatusFilter = 'any' | 'active' | 'finished' | TicketStatus;

export interface TicketFilter {
  status: StatusFilter;
  priority: TicketPriority | 'any';
  search: string;
}

export const NO_FILTER: TicketFilter = { status: 'any', priority: 'any', search: '' };

/** The status picker's entries, in order: the three groupings, then every status in workflow order. */
export const STATUS_FILTERS: readonly { label: string; value: StatusFilter }[] = [
  { label: 'Any status', value: 'any' },
  { label: 'Active (not finished)', value: 'active' },
  { label: 'Finished', value: 'finished' },
  ...ALL_TICKET_STATUSES.map(s => ({ label: statusLabel(s), value: s as StatusFilter }))
];

export function isFiltering(filter: TicketFilter): boolean {
  return filter.status !== 'any' || filter.priority !== 'any' || filter.search.trim() !== '';
}

/**
 * Every word of the search must appear somewhere: the number (with or without `#`), title,
 * description, customer, or who raised it.
 */
export function ticketMatches(t: TicketDto, filter: TicketFilter, customerName = ''): boolean {
  if (filter.status === 'active' && isFinished(t.status)) return false;
  if (filter.status === 'finished' && !isFinished(t.status)) return false;
  if (typeof filter.status === 'number' && t.status !== filter.status) return false;
  if (filter.priority !== 'any' && t.priority !== filter.priority) return false;

  const words = filter.search.trim().toLowerCase().split(/\s+/).filter(w => w !== '');
  if (words.length === 0) return true;
  const haystack = [
    `#${t.id}`,
    t.title,
    t.description,
    customerName,
    t.contactName ?? '',
    t.contactEmail ?? '',
    t.sourceName ?? '',
    t.sourceEmail ?? ''
  ]
    .join('\n')
    .toLowerCase();
  return words.every(w => haystack.includes(w) || (/^\d+$/.test(w) && String(t.id) === w));
}
